'use client';

/**
 * NotificationCenter (WEB-436 → WEB-635)
 *
 * Hosts the presentational `NotificationPanel` (WEB-434) and chooses the
 * surface by breakpoint:
 *
 *   - ≥768px  → a Radix `Popover` dropdown anchored near the header actions
 *   - <768px  → a bottom `Sheet`
 *
 * WEB-635 retired the AppHeader `notification-bell-trigger`. Activation now
 * comes from the nav-menu Notifications row via `useNotificationPanel` — the
 * same module-store pattern as `useSearchSheet`. This component mounts NO
 * visible bar trigger.
 *
 * ─── DATA FLOW ───────────────────────────────────────────────────────────────
 * The menu-row count pill reads `useGetUnreadCountQuery` separately (WEB-635).
 * This host still reads the same query only for the (retired) pulse path's
 * mark-read invalidation consumers; the list comes from
 * `useGetNotificationsQuery`. Both stay fresh via the single canonical
 * `userNotifications` WS subscription at `NotificationToastBridge` (WEB-435).
 * This component mounts NO subscription of its own.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ─── FOCUS MANAGEMENT (coordination with WEB-434 / WEB-635) ──────────────────
 * The panel is hosted INSIDE Radix's Popover/Sheet, which owns the focus trap.
 * We pass `manageFocus={false}` so the panel does NOT run its own outer
 * `document.activeElement` capture/restore. When opened from the menu, the
 * menu host sets `restoreFocusOnClose={false}` so focus hands off into this
 * surface rather than bouncing back to the hamburger.
 *
 * Both surfaces are controlled (no `SheetTrigger` / `PopoverTrigger`), so Radix
 * cannot restore focus on close by itself. Capture the opener in
 * `onOpenAutoFocus` and return focus to it (or the surviving menu trigger) in
 * `onCloseAutoFocus` — same pattern as SearchSheet (FRE-829).
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Open/close: menu row activation (and Escape / outside-click / route change)
 * drive the shared store. The list is only fetched while open
 * (`skip: !open`) so the cold app shell doesn't pay for a notifications
 * request before the user asks for it.
 *
 * Mark-read-on-open: the panel fires `onMarkReadOnOpen` once per open with the
 * visible unread ids; we forward them to `markRead({ ids })`, which invalidates
 * the `Notifications` tag → menu pills refetch and reflect the change.
 */

import * as React from 'react';
import { usePathname, useRouter } from 'next/navigation';
import type { Route } from 'next';
import type { CityCode } from '@loci/city-editions';
import { useGetNotificationsQuery, useMarkReadMutation } from '@loci/api';

import { Popover, PopoverAnchor, PopoverContent } from '@/components/atoms/primitives/popover';
import { Sheet, SheetContent } from '@/components/atoms/primitives/sheet';
import { useIsMobile } from '@/components/atoms/primitives/use-mobile';
import { NotificationPanel } from '@/components/organisms/NotificationPanel/NotificationPanel';
import { useNotificationPanel } from '@/hooks/useNotificationPanel';
import { getCityTimezone } from '@/lib/home/cityTimezone';

export interface NotificationCenterProps {
  /** Authenticated user id. Empty / null hides the center entirely. */
  readonly userId: string | null;
  /** Active city — resolves the IANA timezone for Today/Earlier grouping. */
  readonly cityCode: CityCode;
  readonly className?: string;
}

/**
 * Surviving opener after the menu→panel handoff (WEB-635), in PRIORITY order —
 * `querySelector` on a selector list would return the first match in document
 * order instead. FRE-1389: on the city surface Notifications lives in the
 * signed-in avatar's account drawer, so the avatar is the opener; the city
 * hamburger (now rendered for signed-in visitors too) is only a last resort.
 */
const PANEL_OPENER_SELECTORS = [
  '[data-testid="city-header-account"][data-auth="authed"]',
  '[data-testid="app-menu-trigger"]',
  '[data-testid="city-menu-trigger"]',
] as const;

function resolveMenuTrigger(): HTMLElement | null {
  for (const selector of PANEL_OPENER_SELECTORS) {
    const el = document.querySelector(selector);
    if (el instanceof HTMLElement) return el;
  }
  return null;
}

/**
 * Footer with the "See all" link to the full notification history page
 * (`/{city}/notifications`, WEB-439). Always rendered now that the page exists
 * — the M1 `NEXT_PUBLIC_NOTIFICATIONS_SEE_ALL` build flag was removed when the
 * destination landed.
 */
function SeeAllFooter({
  href,
  onNavigate,
}: {
  href: Route;
  onNavigate: () => void;
}): React.ReactElement {
  const router = useRouter();
  return (
    <footer className="border-t p-2">
      <button
        type="button"
        data-testid="notification-see-all"
        onClick={() => {
          onNavigate();
          router.push(href);
        }}
        className="text-primary hover:bg-muted/60 focus-visible:ring-ring w-full rounded-md px-3 py-2 text-center text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none"
      >
        See all notifications
      </button>
    </footer>
  );
}

/**
 * Connected notification center. Reads the list from RTK Query and hosts the
 * presentational `NotificationPanel` in a breakpoint-appropriate Radix
 * surface. Open state is owned by `useNotificationPanel` (menu activation).
 * Returns `null` when signed out.
 */
export function NotificationCenter({
  userId,
  cityCode,
  className,
}: NotificationCenterProps): React.ReactElement | null {
  const isSignedOut = userId === null || userId === '';
  const isMobile = useIsMobile();
  const pathname = usePathname();
  const router = useRouter();
  const cityTz = getCityTimezone(cityCode);
  const { open, setOpen } = useNotificationPanel();

  // List is fetched only while the surface is open: the cold shell pays for the
  // unread count on the menu pill but not the full list until asked.
  const {
    data: listData,
    isLoading,
    isError,
    refetch,
  } = useGetNotificationsQuery(undefined, { skip: isSignedOut || !open });
  const notifications = listData?.items ?? [];

  const [markRead] = useMarkReadMutation();

  // Close on route change so a deep-link navigation (or any nav) dismisses the
  // surface. Skip the initial mount so we don't fight the open click.
  const didMountRef = React.useRef(false);
  React.useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setOpen(false);
  }, [pathname, setOpen]);

  // Signed-out sessions must not leave a stale open bit in the module store.
  React.useEffect(() => {
    if (isSignedOut && open) setOpen(false);
  }, [isSignedOut, open, setOpen]);

  const handleMarkReadOnOpen = React.useCallback(
    (unreadIds: string[]) => {
      if (unreadIds.length === 0) return;
      // Fire-and-forget: the mutation invalidates the Notifications tag, so the
      // menu count pill + list refetch and reflect the read state.
      void markRead({ ids: unreadIds });
    },
    [markRead],
  );

  const handleActivateRow = React.useCallback(
    (id: string, href: string | null) => {
      void markRead({ ids: [id] });
      setOpen(false);
      if (href) {
        // `href` is validated safe + internal by `hrefFromData` upstream.
        router.push(href as Route);
      }
    },
    [markRead, router, setOpen],
  );

  // Controlled Sheet/Popover have no Trigger — capture the opener (or surviving
  // menu hamburger) so close can restore focus instead of landing on <body>.
  const previouslyFocusedRef = React.useRef<HTMLElement | null>(null);

  const handleOpenAutoFocus = React.useCallback((_event: Event) => {
    const active = document.activeElement;
    if (active instanceof HTMLElement && active !== document.body) {
      previouslyFocusedRef.current = active;
      return;
    }
    previouslyFocusedRef.current = resolveMenuTrigger();
  }, []);

  const handleCloseAutoFocus = React.useCallback((event: Event) => {
    event.preventDefault();
    const target = previouslyFocusedRef.current;
    if (target && document.contains(target)) {
      target.focus();
      return;
    }
    resolveMenuTrigger()?.focus();
  }, []);

  if (isSignedOut) return null;

  const panel = (
    <NotificationPanel
      notifications={notifications}
      isLoading={isLoading}
      isError={isError}
      cityTz={cityTz}
      onMarkReadOnOpen={handleMarkReadOnOpen}
      onActivateRow={handleActivateRow}
      onRetry={refetch}
      onClose={() => setOpen(false)}
      // CRITICAL (WEB-436): defer focus capture/restore to Radix so the panel's
      // own outer document.activeElement capture/restore doesn't fight it.
      manageFocus={false}
    />
  );

  const footer = (
    <SeeAllFooter
      href={`/${cityCode.toLowerCase()}/notifications` as Route}
      onNavigate={() => setOpen(false)}
    />
  );

  // WEB-611 / WEB-635: ride `--z-modal` (same as SearchSheet) so the panel
  // surfaces above the nav-menu takeover during the close→open handoff.
  // Atom defaults are `z-50`; NavMenuSheet is `z-[var(--z-modal)]` (100).
  const modalZ = 'z-modal';

  // Bottom sheet at <768px. No trigger — open state is store-driven (WEB-635).
  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          title="Notifications"
          description="Your recent notifications"
          overlayClassName={modalZ}
          className={['max-h-[80vh] gap-0 p-0', modalZ, className].filter(Boolean).join(' ')}
          data-testid="notification-center-sheet"
          onOpenAutoFocus={handleOpenAutoFocus}
          onCloseAutoFocus={handleCloseAutoFocus}
        >
          <div className="flex max-h-[80vh] min-h-0 flex-col">
            {panel}
            {footer}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Dropdown popover at ≥768px. Anchored to a fixed point near the header
  // actions cluster (where the retired bell lived) so align=end still reads
  // as a right-rail panel without a visible trigger.
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <span
          aria-hidden="true"
          data-testid="notification-center-anchor"
          className="pointer-events-none fixed top-3 right-4 size-0"
        />
      </PopoverAnchor>
      <PopoverContent
        align="end"
        sideOffset={8}
        // Radix stamps `role="dialog"` on the content; give it an accessible
        // name so it doesn't fail aria-dialog-name.
        aria-label="Notifications"
        className={['w-[22rem] max-w-[calc(100vw-2rem)] gap-0 p-0', modalZ, className]
          .filter(Boolean)
          .join(' ')}
        data-testid="notification-center-popover"
        onOpenAutoFocus={handleOpenAutoFocus}
        onCloseAutoFocus={handleCloseAutoFocus}
      >
        {panel}
        {footer}
      </PopoverContent>
    </Popover>
  );
}
